import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Pause, Volume2 } from "lucide-react";
import { Button as MuiButton, LinearProgress, Box, Paper, Typography, createTheme, ThemeProvider } from '@mui/material';
import FlipCard from "./FlipCard"; 

// Assuming ThemeWrapper.tsx provides this context
import { useThemeContext } from './ThemeWrapper';

// --- Custom Button Component (using MUI and Framer Motion) ---
export const Button: React.FC<{ children: React.ReactNode; className?: string; onClick?: () => void; disabled?: boolean }> = ({ children, className, ...props }) => (
    <motion.button
        className={`font-sans text-lg font-semibold border-none rounded-xl p-3 bg-white text-pink-500 shadow-sm cursor-pointer transition-all hover:bg-pink-50 hover:text-pink-400 dark:bg-gray-700 dark:text-pink-300 dark:hover:bg-gray-600 ${className || ""}`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        {...props}
    >
        {children}
    </motion.button>
);

// --- Progress Bar Component (using MUI LinearProgress and Framer Motion) ---
export const Progress: React.FC<{ value: number }> = ({ value }) => (
    <Box sx={{ flexGrow: 1, height: 8, borderRadius: 10, overflow: 'hidden', backgroundColor: 'pink.50', ml: 2, mr: 1 }}>
        <LinearProgress
            variant="determinate"
            value={value}
            sx={{
                height: '100%',
                borderRadius: 10,
                transition: 'width 0.4s cubic-bezier(.68,-0.55,.27,1.55)',
                '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #ffb6c1 0%, #ffb347 100%)',
                },
            }}
        />
    </Box>
);


// --- Main Quiz Component ---
const QuizComponent = ({ chapterId: propChapterId }: { chapterId?: string }) => {
    const params = useParams();
    const chapterId = propChapterId || params.chapterId;
    const { isDarkTheme } = useThemeContext();

    const [quizData, setQuizData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selected, setSelected] = useState<number | null>(null);
    const [showResult, setShowResult] = useState(false);
    const [flipped, setFlipped] = useState(false); // Initial state for FlipCard
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        // Fetch quiz data from public/quizData.json
        fetch("/quizData.json")
            .then((res) => {
                if (!res.ok) throw new Error("Failed to load quiz data");
                return res.json();
            })
            .then((data) => {
                setQuizData(data);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    // Reset states when moving to the next question
    useEffect(() => {
        setSelected(null);
        setShowResult(false);
        setFlipped(false); // Ensure card is unflipped for new question
    }, [currentIndex]);

    // Material-UI theme for dark mode compatibility
    const muiTheme = createTheme({
        palette: {
            mode: isDarkTheme ? 'dark' : 'light',
            primary: {
                main: '#ff69b4', // Pink for primary actions
            },
            secondary: {
                main: '#ffb347', // Orange for secondary actions
            },
            background: {
                default: isDarkTheme ? '#1a202c' : '#fff0f6',
                paper: isDarkTheme ? '#2d3748' : '#fff',
            },
            text: {
                primary: isDarkTheme ? '#e2e8f0' : '#2d3748',
                secondary: isDarkTheme ? '#a0aec0' : '#718096',
            },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '1.2rem',
                        textTransform: 'none',
                        fontWeight: 600,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderRadius: '2rem',
                    },
                },
            },
        },
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-pink-100 to-blue-100 dark:from-gray-900 dark:to-gray-800">
                <Paper elevation={3} className="p-8 rounded-3xl shadow-lg max-w-sm w-full flex flex-col items-center justify-center min-h-[300px] dark:bg-gray-800 dark:text-gray-200">
                    <motion.span
                        className="text-5xl"
                        animate={{ y: [0, -12, 0] }}
                        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                    >
                        🍡
                    </motion.span>
                    <Typography variant="h6" className="mt-4 text-pink-500 dark:text-pink-300">Loading...</Typography>
                </Paper>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-pink-100 to-blue-100 dark:from-gray-900 dark:to-gray-800">
                <Paper elevation={3} className="p-8 rounded-3xl shadow-lg max-w-sm w-full flex flex-col items-center justify-center min-h-[300px] dark:bg-gray-800 dark:text-gray-200">
                    <Typography variant="h6" color="error" className="text-center">
                        Error: {error}
                    </Typography>
                </Paper>
            </div>
        );
    }

    const questions = quizData.filter(q => String(q.chapter) === String(chapterId));
    const currentQuestion = questions[currentIndex];

    if (!questions.length) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-pink-100 to-blue-100 dark:from-gray-900 dark:to-gray-800">
                <Paper elevation={3} className="p-8 rounded-3xl shadow-lg max-w-sm w-full flex flex-col items-center justify-center min-h-[300px] dark:bg-gray-800 dark:text-gray-200">
                    <Typography variant="h6" className="text-pink-500 font-bold text-center dark:text-pink-300">
                        No questions found for this chapter.
                    </Typography>
                </Paper>
            </div>
        );
    }

    if (!currentQuestion) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-pink-100 to-blue-100 dark:from-gray-900 dark:to-gray-800">
                <Paper elevation={3} className="p-8 rounded-3xl shadow-lg max-w-sm w-full flex flex-col items-center justify-center min-h-[300px] dark:bg-gray-800 dark:text-gray-200">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ type: "spring", stiffness: 100, damping: 10 }}
                        className="text-pink-500 font-bold text-center text-2xl dark:text-pink-300"
                    >
                        🎉 Congratulations! You have completed the chapter! 🎉
                    </motion.div>
                </Paper>
            </div>
        );
    }

    const handleChoice = (choice: any, idx: number) => {
        setSelected(idx);
        if (choice.correct) {
            setTimeout(() => setCurrentIndex((prev) => prev + 1), 900);
        } else {
            setShowResult(true);
        }
    };

    const renderChoices = () => (
        <div className="w-full flex flex-col gap-3 mb-2">
            {currentQuestion.choices.map((choice: any, idx: number) => (
                <MuiButton
                    key={idx}
                    variant="outlined"
                    className={`w-full text-lg font-semibold p-3 rounded-xl border-2 transition-all text-left flex items-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed
                        ${selected !== null
                            ? choice.correct
                                ? "!bg-green-100 !border-green-400 !text-green-700 dark:!bg-green-800 dark:!border-green-600 dark:!text-green-200"
                                : selected === idx
                                    ? "!bg-red-100 !border-red-400 !text-red-700 dark:!bg-red-800 dark:!border-red-600 dark:!text-red-200"
                                    : "bg-white border-pink-50 text-pink-500 dark:bg-gray-700 dark:border-gray-600 dark:text-pink-300"
                            : "bg-white border-pink-50 text-pink-500 hover:bg-pink-50 hover:text-pink-400 dark:bg-gray-700 dark:border-gray-600 dark:text-pink-300 dark:hover:bg-gray-600"
                        }`}
                    onClick={() => handleChoice(choice, idx)}
                    disabled={selected !== null}
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    {choice.reading ? (
                        <span className="flex flex-col items-start">
                            <span className="text-base text-pink-300 mr-2 dark:text-pink-200">{choice.reading}</span>
                            <span className="text-lg font-bold">{choice.text}</span>
                        </span>
                    ) : (
                        <span className="text-lg font-bold">{choice.text}</span>
                    )}
                </MuiButton>
            ))}
        </div>
    );

    const renderCard = () => {
        if (currentQuestion.type === "flipcard") {
            return (
                <motion.div
                    key={currentIndex} // Key to re-mount and animate on next question
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="w-full flex flex-col items-center gap-8 mb-6"
                >
                    <FlipCard
                        front={currentQuestion.front}
                        back={currentQuestion.back}
                        flipped={flipped}
                        onFlip={() => setFlipped(!flipped)} // Allow flipping the card
                    />
                    <MuiButton
                        variant="contained"
                        color="primary"
                        className="w-full text-xl font-bold p-4 rounded-xl shadow-md bg-gradient-to-r from-pink-400 to-orange-400 hover:from-orange-400 hover:to-pink-400"
                        onClick={() => setCurrentIndex((prev) => prev + 1)}
                        component={motion.button}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Next
                    </MuiButton>
                </motion.div>
            );
        }

        return (
            <motion.div
                key={currentIndex} // Key to re-mount and animate on next question
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="w-full flex flex-col items-center"
            >
                <Typography variant="h5" className="font-bold text-pink-500 mb-3 text-center dark:text-pink-400">
                    {currentQuestion.question}
                </Typography>
                {currentQuestion.image && (
                    <img
                        src={currentQuestion.image}
                        alt="question"
                        className="w-32 h-32 object-contain bg-pink-50 rounded-xl mb-4 shadow-md dark:bg-gray-700"
                    />
                )}
                {currentQuestion.audio && (
                    <MuiButton
                        variant="contained"
                        sx={{
                            minWidth: 'auto',
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            mb: 2,
                            backgroundColor: isDarkTheme ? '#4a5568' : '#fff0f6',
                            color: isDarkTheme ? '#e2e8f0' : '#ff69b4',
                            '&:hover': {
                                backgroundColor: isDarkTheme ? '#2d3748' : '#ffe7f7',
                            },
                        }}
                        onClick={() => new Audio(currentQuestion.audio).play()}
                        component={motion.button}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <Volume2 size={28} />
                    </MuiButton>
                )}
                <Typography variant="body1" className="text-lg text-pink-400 mb-5 font-medium text-center dark:text-pink-300">
                    {currentQuestion.prompt}
                </Typography>
                {renderChoices()}
                <AnimatePresence>
                    {showResult && selected !== null && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="text-red-600 font-semibold mt-4 mb-2 bg-red-50 rounded-xl p-3 shadow-sm dark:bg-red-900 dark:text-red-200"
                        >
                            ❌ Wrong answer. Try to remember it!
                        </motion.div>
                    )}
                </AnimatePresence>
                {(showResult || currentQuestion.type === "flipcard") && (
                    <MuiButton
                        variant="contained"
                        color="primary"
                        className="w-full text-xl font-bold p-4 rounded-xl shadow-md bg-gradient-to-r from-pink-400 to-orange-400 hover:from-orange-400 hover:to-pink-400"
                        onClick={() => setCurrentIndex((prev) => prev + 1)}
                        component={motion.button}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Next
                    </MuiButton>
                )}
            </motion.div>
        );
    };

    return (
        <ThemeProvider theme={muiTheme}>
            <div className="min-h-screen flex items-center justify-center p-4 sm:p-8 bg-gradient-to-br from-pink-100 to-blue-100 dark:from-gray-900 dark:to-gray-800">
                <Paper elevation={3} className="p-6 sm:p-8 rounded-3xl shadow-lg max-w-md w-full min-h-[620px] flex flex-col items-center relative dark:bg-gray-800 dark:text-gray-200">
                    <div className="w-full flex items-center mb-6 gap-4">
                        <MuiButton
                            variant="outlined"
                            sx={{
                                minWidth: 'auto',
                                padding: '0.5rem 1rem',
                                borderRadius: '1rem',
                                borderColor: isDarkTheme ? '#a0aec0' : '#f9c6e0',
                                backgroundColor: isDarkTheme ? '#4a5568' : '#f8f8f8',
                                '&:hover': {
                                    borderColor: isDarkTheme ? '#cbd5e0' : '#ffb6c1',
                                    boxShadow: isDarkTheme ? '0 4px 16px rgba(0,0,0,0.2)' : '0 4px 16px rgba(249, 198, 224, 0.4)',
                                },
                            }}
                            onClick={() => setPaused((p) => !p)}
                            component={motion.button}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Pause className={paused ? "text-orange-500" : "text-gray-400 dark:text-gray-300"} />
                        </MuiButton>
                        <Progress value={((currentIndex + 1) / questions.length) * 100} />
                    </div>
                    {renderCard()}
                </Paper>
            </div>
        </ThemeProvider>
    );
};

export default QuizComponent;